import { all, call, put, takeLatest, select, delay } from 'redux-saga/effects';
import i18n from 'i18next';
import _isEmpty from 'lodash/isEmpty';
import { processRequest } from '../../services/Api';
import { setGTMData, setLocalStorageItem } from '../../services/Helper';
import { historyReplace } from '../../services/History';
import { getUpgradeModalIdentifier } from './Selectors';
import { SOURCE_TYPES } from '../../services/Constants';
import { packagesActionTypes } from './Constants';
import * as packagesActions from './Actions';
import * as sharedActions from '../../pagesNext/shared/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as sidebarActions from '../../components/sideBar/Actions';
import * as notificationActions from '../../components/notification/Actions';
import { handlePublicExperiment } from '../publicShop/Sagas';

export default function* () {
	yield all([
		yield takeLatest(packagesActionTypes.GET_CHOOSE_PACKAGE, handleChoosePackagesRequest),
		yield takeLatest(packagesActionTypes.GET_MY_PACKAGE, handleMyPackagesRequest),
		yield takeLatest(
			packagesActionTypes.CANCEL_SUBSCRIPTION_REQUEST,
			handleCancelSubscriptionRequest,
		),
		yield takeLatest(
			packagesActionTypes.CHOOSE_FREE_PACKAGE_REQUEST,
			handleChooseFreePackageRequest,
		),
		yield takeLatest(
			packagesActionTypes.REACTIVATE_SUBSCRIPTION_REQUEST,
			handleReactivateSubscriptionRequest,
		),
	]);
}

export function* handleChoosePackagesRequest({ payload }) {
	try {
		const { identifier } = payload;

		if (identifier) yield call(handlePublicExperiment, 'choose-package');

		const { data } = yield call(
			processRequest,
			identifier ? `payment_requests/${identifier}/packages` : 'public/packages',
		);

		yield put(packagesActions.getChoosePackageSuccess(data));
	} catch (e) {
		const { status, data } = e.response || {};
		if (status === 400 && data && data.hasOwnProperty('email')) {
			yield call(historyReplace, '/already-paid', data);
		}
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleMyPackagesRequest({ payload }) {
	try {
		const { last_chance_key } = payload || {};

		const upgradeModalIdentifier = yield select(getUpgradeModalIdentifier);
		const { data } = yield call(processRequest, 'public/subscriptions', 'GET', {
			source: upgradeModalIdentifier || SOURCE_TYPES.MENU,
			last_chance_key,
		});

		yield put(sharedActions.setUpgradeModalIdentifier({ upgradeModalIdentifier: null }));
		yield put(packagesActions.getMyPackageSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(packagesActions.getMyPackageError());
	}
}

export function* handleCancelSubscriptionRequest({ payload }) {
	const { date, eventData } = payload;
	const reqPayload = {
		cancellation_date: date,
		...(eventData ? { event_data: eventData } : {}),
	};
	try {
		yield call(processRequest, 'subscriptions/cancel', 'DELETE', reqPayload);

		setGTMData({
			event: 'packageCancellation',
		});
		const { data: subscriptionsData } = yield call(processRequest, 'subscriptions', 'GET', {
			source: SOURCE_TYPES.MY_PACKAGES,
		});
		yield put(packagesActions.getMyPackageSuccess(subscriptionsData));
		yield put(packagesActions.cancelSubscriptionSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(packagesActions.cancelSubscriptionError(e));
	}
}

export function* handleChooseFreePackageRequest({ payload }) {
	try {
		const { packageId } = payload;

		const { data } = yield call(processRequest, `public/packages/${packageId}/choose`, 'POST');

		const { applicable_offer, show_parent_details_offer } = data || {};

		if (data && applicable_offer) {
			if (show_parent_details_offer) setLocalStorageItem('showParentDetailsOffer', true);
			yield call(historyReplace, `/offer/${applicable_offer}`);
		} else {
			yield call(historyReplace, '/');
			yield put(sidebarActions.getUseDataRequest());
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(packagesActions.handleChooseFreePackageError());
	}
}

export function* handleReactivateSubscriptionRequest({ payload }) {
	const { last_chance_key, callback } = payload || {};

	try {
		yield call(processRequest, 'public/subscriptions/reactivate', 'POST', { last_chance_key });

		let isActive = false;
		let subscriptionData = {};
		let counter = 0;
		const timeouts = [500, 1000, 2000, 4000];

		while (!isActive && counter < 4) {
			yield delay(timeouts[counter]);
			const { data } = yield call(processRequest, 'public/subscriptions', 'GET', {
				source: SOURCE_TYPES.MY_PACKAGES,
				last_chance_key,
			});
			subscriptionData = data;
			counter += 1;
			isActive = data.subscriptions[0].stop_package;
		}

		if (!_isEmpty(subscriptionData)) {
			yield put(packagesActions.getMyPackageSuccess(subscriptionData));
		}
		if (counter > 4) {
			yield put(
				notificationActions.createNotification(
					i18n.t('modals.reactivate_package_modal.notification_error'),
					null,
					true,
					5000,
				),
			);
		}

		yield put(
			packagesActions.toggleReactivateSubscriptionModal(
				{ open: false, loading: false },
				{ surveyLink: null },
			),
		);

		if (callback) callback(true);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(packagesActions.reactivateSubscriptionError(e));
		yield put(
			packagesActions.toggleReactivateSubscriptionModal({ open: false, loading: false }),
		);
		if (callback) callback(false);
	}
}
